import React, { useEffect, useRef, useState } from 'react'
import { Upload, message } from 'antd'
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons'
import axios from 'axios'
import { useIntl } from 'react-intl'
import commonApi from '@/api/commonApi'
import styles from './index.module.scss'

interface ObsImageUploadProps {
  value?: string[]
  onChange?: (value: string[]) => void
  tip?: string
  disabled?: boolean
  maxSize?: number
}

const ObsImageUpload: React.FC<ObsImageUploadProps> = (props) => {
  const { value = [], onChange, tip = '拖拽或粘贴图片(可多张)', disabled = false, maxSize = 5 } = props
  const intl = useIntl()
  const [loading, setLoading] = useState<boolean>(false)
  const valueRef = useRef<string[]>(value)
  const loadingCountRef = useRef<number>(0)

  useEffect(() => {
    valueRef.current = Array.isArray(value) ? value : []
  }, [value])

  const triggerChange = (images: string[]) => {
    valueRef.current = images
    onChange && onChange(images)
  }
  const addImageUrl = (url: string) => {
    if(!url) return
    triggerChange([...valueRef.current, url])
  }
  const startLoading = () => {
    loadingCountRef.current += 1
    setLoading(true)
  }
  const finishLoading = () => {
    loadingCountRef.current = Math.max(loadingCountRef.current - 1, 0)
    setLoading(loadingCountRef.current > 0)
  }
  const beforeUpload = (file: any) => {
    const isImage = file.type && file.type.indexOf('image') !== -1
    if(!isImage) {
      message.error(intl.formatMessage({id: '请上传图片'}))
    }
    const isLtMax = file.size / 1024 / 1024 < maxSize
    if(!isLtMax) {
      message.error(intl.formatMessage({id: '上传图片大小必须小于5MB!'}))
    }
    return isImage && isLtMax
  }
  const customRequest = (e: any) => {
    let file = e.file
    let name = file.name
    let onProgress = e.onProgress
    let onSuccess = e.onSuccess
    let onError = e.onError

    commonApi.getObsSign({}).then((res: any) => {
      if(res.code === 1) {
        let resData = res.data ? res.data : {}
        let key = new Date().getTime() + '.' + name
        let policy = resData.policy
        let signature = resData.signature
        let accessKeyId = resData.accessKeyId
        let url = resData.url + '/'
        let cdn = resData.cdn + '/'
        let formData = new FormData()
        formData.append('key', key)
        formData.append('content-type', 'text/plain')
        formData.append('policy', policy)
        formData.append('signature', signature)
        formData.append('accessKeyId', accessKeyId)
        formData.append('file', file)
        startLoading()
        axios.post(url, formData, {
          onUploadProgress: (progressEvent: any) => {
            const total = progressEvent.total || 1
            const percentCompleted = Math.round((progressEvent.loaded * 100) / total)
            onProgress && onProgress({ percent: percentCompleted }, file)
          }
        }).then((res1: any) => {
          let fileUrl = cdn + key
          addImageUrl(fileUrl)
          finishLoading()
          onSuccess && onSuccess(res1, file)
        }).catch((err: any) => {
          finishLoading()
          message.error(intl.formatMessage({id: '上传失败'}))
          onError && onError(err)
        })
      }else {
        message.error(intl.formatMessage({id: '上传失败'}))
        onError && onError(res)
      }
    }).catch((err: any) => {
      message.error(intl.formatMessage({id: '上传失败'}))
      onError && onError(err)
    })
  }
  const uploadPasteFile = (file: File) => {
    if(!beforeUpload(file)) return
    customRequest({
      file,
      onProgress: () => {},
      onSuccess: () => {},
      onError: () => {}
    })
  }
  const uploadPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    if(disabled) return
    let files = Array.from(e.clipboardData.files || []).filter((file: any) => file.type && file.type.indexOf('image') !== -1)
    files.forEach((file: any) => uploadPasteFile(file))
  }

  return (
    <div className={styles['obs-upload']} onPaste={uploadPaste}>
      <Upload.Dragger
        accept=".png, .jpg, .jpeg, .gif"
        multiple
        disabled={disabled}
        beforeUpload={beforeUpload}
        customRequest={customRequest}
        showUploadList={false}
      >
        <div className={styles['upload-area']}>
          {loading ? <LoadingOutlined rev={undefined} /> : <PlusOutlined rev={undefined} />}
          <span>{intl.formatMessage({id: tip})}</span>
        </div>
      </Upload.Dragger>
    </div>
  )
}

export default ObsImageUpload
